import * as Commons from "oc-commons/Commons";
import * as Doms from "oc-ui/Doms";
import { ListenerMixin } from "oc-commons/ListenerMixin";
import { v3 } from "./short3";
import { AbstractStage } from "./AbstractStage";
import * as Maths3 from "./Maths3";
import * as Scenes from "./Scenes";
import { DeviceDetection } from "oc-commons/DeviceDetection";
import Stats from "stats.js";
import toBlob from "canvas-to-blob";
import { saveAs } from "browser-filesaver";
import { OrbitControlsOC } from "oc-threejs/patched/OrbitControlsOC";
import TWEEN from "@tweenjs/tween.js";
import { OcAxisHelper } from "./objects/OcAxisHelper";
import * as Textures from "./Textures";
import "oc-threejs/patched/GLTFExporter.oc";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass";
import { SAOPass } from "three/examples/jsm/postprocessing/SAOPass";
import { VisibleSwitchRenderPass } from "./pass/VisibleSwitchRenderPass";
import "perfnow";
import "./scss/renderStats.scss";
import * as Mimetypes from "oc-commons/Mimetypes";
import * as _ from "lodash";
import { GLTFExporter } from "./patched/GLTFExporter.oc";
import * as THREE from "three";

import { IDefaultStageSettings, IVector3Simple } from "./DefaultStageSettings";
import * as Debugs from "./Debugs";
import {
  AmbientLight,
  AnimationClip,
  AnimationMixer,
  Box3,
  CircleGeometry,
  Clock,
  Color,
  DirectionalLight,
  HemisphereLight,
  Light,
  Material,
  Mesh,
  MeshBasicMaterial,
  MeshLambertMaterial,
  Object3D,
  OrthographicCamera,
  PerspectiveCamera,
  PlaneGeometry,
  PointLight,
  Quaternion,
  RectAreaLight,
  SRGBColorSpace,
  Scene,
  ShadowMapType,
  Sphere,
  SpotLight,
  Texture,
  TextureLoader,
  ToneMapping,
  Vector3,
  WebGLRenderer,
} from "three";

// Type definitions
export interface ISaveCameraPosition {
  object: IVector3Simple;
  target: IVector3Simple;
}

interface IAnimations {
  [key: string]: {
    animations: AnimationClip[];
    mixer: AnimationMixer;
  };
}

interface IMixer {
  [key: string]: any; // Changed from AnimationMixer because it has extra props
  // Actually, let's keep it clean:
  // The original code implies mixer has specialized keys + standard methods
  // But cleaner is:
  // mixer: Record<string, THREE.AnimationMixer> & { animationPlaying?: boolean; update?: (delta: number) => void };
}

// Better IMixer definition based on usage
type IMixerWithExtensions = {
  [key: string]: AnimationMixer | boolean | ((delta: number) => void) | undefined;
  animationPlaying?: boolean;
  update?: (delta: number) => void;
};

export interface ICameraDescription {
  name?: string;
  Typ?: "Zoom" | "Position" | "Orthographic";
  position?: Vector3;
  target?: string | Vector3 | { position: Vector3; jaco?: { box?: Box3 } } | Object3D;
  zoomTargetRadius?: number;
  zoomDirection?: Vector3;
  zoomDirectionWorld?: boolean;
  extraZoom?: number;
  panningAfter?: Vector3;
  orthographic?: boolean;
  up?: Vector3;
  disableRotate?: boolean;
}

export interface ICameraConfig {
  object: IVector3Simple;
  target: IVector3Simple;
  up?: IVector3Simple;
  orthographic?: boolean;
  disableRotate?: boolean;
  zoomTo?: boolean;
  zoomToTarget?: Object3D | Sphere | Box3;
  direction?: IVector3Simple;
  extraZoom?: number;
  radius?: number;
  zoomDirection?: IVector3Simple;
  border?: number;
  canvasSize?: { width: number; height: number };
  sphere?: Sphere;
  hideObjectsByName?: string[];
  showBackgroundImage?: boolean;
  backgroundColor?: number | Color;
  backgroundAlpha?: number;
  prepareStage?: () => void;
  restoreStage?: () => void;
  showObjects?: Object3D[];
  showDebugObjects?: boolean;
  clearColor?: number;
  clearAlpha?: number;
  backgroundTexture?: Texture | null;
}

// THREE.ColorManagement.legacyMode = false; // Deprecated/Removed in newer Three.js versions

toBlob.init();

/**
 * Die von AbstractStage abgeleitete Standard-Stage (siehe dort für einige weitere Methoden).
 * Diese Klasse ist sehr umfangreich, weil sie an einer Stelle die wesentlichen Methoden für die Manipulation der
 * Ansicht der Szene bereitstellt. AbstractStage ist dagegen so minimal wie möglich gehalten.
 *
 * Wesentliche Elemente sind das Erzeugen und Bereitstellen von
 *
 * - Scene
 * - WebGL-Renderer
 * - Camera
 * - OrbitControl
 * - Standard-Lichtern
 * - App-weiten Settings
 *
 * Die Methoden gruppieren sich nach den Themen
 *
 * - Hilfsmethoden für das Hinzufügen von Lichtern, Gitterboden und Koordinatenkreuz.
 * - Renderloop, Dirty-Flag, Verändern der Größe des Renderers und Rendern als Einzelbild
 * - Kamera-Manipulation, Speichern und Wiederherstellen der Kameraposition, Kamerafahrt, Wechsel Orthografisch/Perspektivisch
 * - Anzeige eines Hintergrundbildes
 * - Animator-Feature (Animationen werden als globaler ein/ausschaltbarer Zustand betrachtet,
 *   Objekte sorgen über eine Callback-Methode selbst für die Darstellung der einzelnen Animationsschritte)
 * - Anzeige der Frames-Per-Second-Info
 *
 */
export class Stage extends ListenerMixin(AbstractStage) {
  name: string = "STAGE";
  isDirty: boolean = false;
  stopRendering: boolean = false;
  renderingLoopRuns: boolean = false;
  renderOffline: boolean = false;
  renderLooping: boolean = false;
  stopAnimation: boolean = false;
  tweenTimer: number = 0;
  tweenPrevTime: number = 0;
  renderOfflineIntervalMs: number = 100;

  // wie viele ms benötigte das letzte gerenderte Frame?
  lastRenderSceneMs: number = 0;

  stats: Stats | null = null;

  zoomScale: number = Math.pow(0.95, 0.3);
  locked: boolean = false;

  // timestamp, bis zu dem in jedem requestAnimationFrame neu gerendert wird - praktisch für zeitlich begrenzte Animationen
  isDirtyUntil: number = 0;

  // when switching to innerWallMesh mode, save actual camera
  // position as it is going to change...
  saveCameraPosition: ISaveCameraPosition = {
    object: {
      x: 0,
      y: 0,
      z: 0,
    },
    target: {
      x: 0,
      y: 0,
      z: 0,
    },
  };

  planeYHeight: number | null = null;

  perspectiveCamera!: PerspectiveCamera;
  orthographicCamera!: OrthographicCamera;
  orthographicMode: boolean = false;

  cameralock: boolean = false;

  animations: IAnimations = {};
  mixer: IMixerWithExtensions = {};

  clock: Clock = new Clock();

  _mainObject!: Object3D;

  // Additional properties used throughout the class
  orbitcontrols!: OrbitControlsOC;
  composer?: EffectComposer;
  renderPass?: RenderPass;
  saoPass?: SAOPass;
  useComposer?: boolean;
  animator?: any; // Will be typed strictly later with a class
  secondPassScene: Scene;

  /**
   */
  constructor(settings: IDefaultStageSettings) {
    super(settings);
    this._stageInitialize();
  }

  /**
   */
  _stageInitialize() {
    this.scene = this.createScene();

    // export for https://github.com/jeromeetienne/threejs-inspector
    (window as any).scene = this.scene;

    this.renderer = this.createRenderer();

    this.perspectiveCamera = this.createCamera();
    this.orthographicCamera = this.createOrthographicCamera();
    this.camera = this.perspectiveCamera;

    this.scene.add(this.perspectiveCamera);
    this.scene.add(this.orthographicCamera);

    this.composerHook();
    this.orbitcontrols = this.createOrbitControl();

    if (this.settings.lightSettingsName == "default") {
      this.createLights();
    }

    this._mainObject = this.scene;

    if (this.settings.rendererBackgroundImage) {
      (this as any).addRendererBackgroundImage();
    }

    if (this.settings.animator) {
      this.addAnimator();
    }
    if (this.settings.showFramesPerSecondInfo) {
      (this as any).showFramesPerSecondInfo();
    }

    this.resetOrbitControl();

    STAGE.addAnimator();

    this.dirty();
  }

  //--------------------------------------------------------------------------------------
  // Hauptobjekt
  //--------------------------------------------------------------------------------------

  /**
   * gibt das Hauptobjekt der Szene zurück.
   * Beispiel: Im Schrankplaner ist es der Schrank.
   * In Winnetoo ist es der gesamte Spielplatz.
   * Wird z.B. benutzt, um mit der Kamera draufzuzoomen.
   */
  get mainObject(): Object3D {
    return this._mainObject;
  }

  set mainObject(value: Object3D) {
    this._mainObject = value;
  }

  //--------------------------------------------------------------------------------------
  // Create-Methoden der Standardobjekte der Stage
  //--------------------------------------------------------------------------------------

  /** */
  /** */
  createScene(): Scene {
    return new Scene();
  }

  /** */
  createCamera(): PerspectiveCamera {
    const cameraSettings = this.settings.camera;

    return new PerspectiveCamera(
      cameraSettings.fov,
      //divContainer.width() / divContainer.height(),
      1,
      cameraSettings.near,
      cameraSettings.far,
    );
  }

  /** */
  createOrthographicCamera(): OrthographicCamera {
    return new OrthographicCamera(
      -1,
      1,
      1,
      -1,
      STAGE.settings.camera.near,
      STAGE.settings.camera.far,
    );
  }

  /** */
  createRenderer(): WebGLRenderer {
    const renderer = new WebGLRenderer({
      antialias: this.settings.renderer.antialias,
      alpha: this.settings.renderer.alpha,
      // niemals true setzen -> performance!
      preserveDrawingBuffer: false,
    });

    // FIXME temporary workaround
    // if (DeviceDetection.firefox) {
    //     console.warn('Achtung, das hier bitte bald rausnehmen, dient nur zum Ausschalten von überflüssigen Warnings im FF: DISABLING renderer.context.getShaderInfoLog, see https://github.com/mrdoob/three.js/issues/9716');
    //     renderer.getContext().getShaderInfoLog = () => '';
    // }

    renderer.sortObjects = this.settings.renderer.sortObjects;

    renderer.getContext().canvas.addEventListener(
      "webglcontextlost",
      (event) => {
        console.warn("webglcontextlost", event);
      },
      false,
    );

    if (this.settings.shadows.enabled) {
      renderer.shadowMap.enabled = true;
      const shadowMapType = this.settings.shadows.shadowMapType as keyof typeof THREE;
      renderer.shadowMap.type = THREE[shadowMapType] as unknown as ShadowMapType;
      // renderer.logarithmicDepthBuffer = this.settings.renderer.logarithmicDepthBuffer; // Property does not exist on WebGLRenderer, only on params
    }

    // https://www.khronos.org/webgl/wiki/HandlingHighDPI
    // todo mal auf retina device checken! evtl. muss die canvas.with/height ebenfalls angepasst werden
    renderer.setPixelRatio(this.devicePixelRatio * this.settings.ratioFactor);

    // renderer.setPixelRatio( window.devicePixelRatio );
    this.setClearColor(
      this.getClearColorFromSettings(),
      this.settings.renderer.clearColor.alpha,
      renderer,
    );

    // set this for correct color output. The defaults of threejs are old and were never changed
    // https://discourse.threejs.org/t/whats-this-about-gammafactor/4264/2

    if (this.settings.renderer.toneMapping) {
      const toneMapping = this.settings.renderer.toneMapping as keyof typeof THREE;
      renderer.toneMapping = THREE[toneMapping] as unknown as ToneMapping;
    }
    renderer.toneMappingExposure = this.settings.renderer.exposure;
    renderer.outputColorSpace = SRGBColorSpace;

    return renderer;
  }

  getClearColorFromSettings(): Color {
    let c = this.settings.renderer.clearColor.color;
    if (c == null) {
      c = 0xffffff;
    }
    return new Color(c);
  }

  setClearColor(
    color: Color | number = new Color(0xffffff),
    alpha: number = 1,
    renderer: WebGLRenderer = this.renderer,
  ): void {
    if (alpha < 0.999 && !STAGE.settings.renderer.alpha) {
      console.error(
        "renderer.setClearColor with transparent alpha needs STAGE.settings.renderer.alpha=true, but is false. The generated image will not be transparent.",
      );
    }
    renderer.setClearColor(color, alpha);
  }

  get devicePixelRatio(): number {
    return Math.min(window.devicePixelRatio || 1, this.settings.maxDevicePixelRatio || 2);
  }

  /** */
  createOrbitControl(): OrbitControlsOC {
    const cameraSettings = this.settings.camera;

    const orbitControls = new OrbitControlsOC(this.camera, this.canvas);
    orbitControls.addEventListener("change", () => this.dirty());

    orbitControls.polarAngleLocked = cameraSettings.polarAngle.locked;
    orbitControls.minPolarAngle = Maths3.angelDegToRad(cameraSettings.polarAngle.min);
    orbitControls.maxPolarAngle = Maths3.angelDegToRad(cameraSettings.polarAngle.max);

    orbitControls.minAzimuthAngle = Maths3.angelDegToRad(cameraSettings.azimuthAngle.min);
    orbitControls.maxAzimuthAngle = Maths3.angelDegToRad(cameraSettings.azimuthAngle.max);

    orbitControls.minCameraTargetPosition = cameraSettings.minCameraTargetPosition;
    orbitControls.maxCameraTargetPosition = cameraSettings.maxCameraTargetPosition;
    orbitControls.minCameraPosition = cameraSettings.minCameraPosition;
    orbitControls.maxCameraPosition = cameraSettings.maxCameraPosition;

    orbitControls.zoomSpeed = cameraSettings.zoomSpeed;
    orbitControls.rotateSpeed = cameraSettings.rotateSpeed;
    orbitControls.enablePan = !cameraSettings.disablePan;
    orbitControls.enableDamping = cameraSettings.enableDamping;
    orbitControls.dampingFactor = cameraSettings.dampingFactor;
    this.on("beforeRenderScene", () => {
      if (orbitControls.update()) {
        this.setDirtyForMs(600);
      }
    });

    return orbitControls;
  }

  /** */
  createLights(): Light[] {
    let lights: Light[] = [];
    lights.push(this.addAmbientLightIntensity(3));
    lights.push(this.addDirectionalLightsAround(3, 20, 3));
    return lights;
  }

  //--------------------------------------------------------------------------------------
  // Hilfsmethoden für das Hinzufügen von Lichtern
  //--------------------------------------------------------------------------------------

  /**
   * Erzeugt eine Anzahl von DirectionalLights um die Szenenmitte herum gleichmäßig verteilt
   * @param lights Anzahl der erzeugten Lichter
   * @param azimuth Höhe als Winkel in Grad (90°==Zenith)
   * @param intensityPerLight (0..1)
   */
  addDirectionalLightsAround(
    lights: number = 3,
    azimuth: number = 20,
    intensityPerLight: number = 1,
  ): DirectionalLight {
    const v = v3(0, 0, -1);
    v.applyAxisAngle(Maths3.vector.x, (azimuth * Math.PI) / 180);
    this.addDirectionalLight(v.x, v.y, v.z, intensityPerLight);
    const polar = 360 / lights;
    // Note: This function only returns the LAST created light because the original code didn't return an array but the loop's last result was implicitly returned or undefined.
    // However, the caller push(this.addDirectionalLightsAround(...)) suggests it expects one light or undefined.
    // But existing JS code: loops and calls addDirectionalLight. `this.addDirectionalLight` returns light.
    // The loop body calls it but doesn't collect them unless line 410 handles the first one.
    // Actually `addDirectionalLightsAround` calls `addDirectionalLight` multiple times.
    // Line 392 calls `lights.push(this.addDirectionalLightsAround(...))`.
    // If it returns the last light, that's fine for the array type.
    let lastLight: DirectionalLight | null = null;

    // First light
    lastLight = this.addDirectionalLight(v.x, v.y, v.z, intensityPerLight);

    for (let i = 1; i < lights; i++) {
      v.applyAxisAngle(Maths3.vector.y, (polar * Math.PI) / 180);
      lastLight = this.addDirectionalLight(v.x, v.y, v.z, intensityPerLight);
    }
    return lastLight!;
  }

  /**
   * Sucht in der Scene nach Lightquellen die dem Namen entsprechen
   * @param {String} name
   * @returns Array
   */
  getLightByName(name: string = ""): Object3D[] {
    return Scenes.filter(this.scene, (o) => o.name == name && o.type.includes("Light")) || [];
  }

  /**
   * Erzeugt ein graues AmbientLight mit einer bestimmten Intensität von 0..1
   */
  addAmbientLightIntensity(intensity: number): AmbientLight {
    const b = Math.round(intensity * 255);
    return this.addAmbientLight((b << 16) | (b << 8) | b);
  }

  /**
   * Erzeugt ein AmbientLigt einer bestimmten Farbe
   */
  addAmbientLight(color: number = 0xffffff, intensity: number = 1): AmbientLight {
    //console.log('addAmbientLight(color=0x'+color.toString(16)+')');
    let light = new AmbientLight(color, intensity);
    this.scene.add(light);
    this.maybeAddLightHelper(light);
    return light;
  }

  maybeAddLightHelper(light: Light): void {
    if (APP.settings.stage.showDebugObjects) {
      Debugs.lightHelper(light);
    }
  }

  /**
   * Fügt ein DirectionalLight hinzu, das an einer bestimmten Position steht und auf den Nullpunkt gerichtet ist
   */
  /**
   * Fügt ein DirectionalLight hinzu, das an einer bestimmten Position steht und auf den Nullpunkt gerichtet ist
   */
  addDirectionalLight(
    x: number,
    y: number,
    z: number,
    intensity: number = 1,
    color: number = 0xffffff,
  ): DirectionalLight {
    // console.log('addDirectionalLight(position=('+x+','+y+','+z+'), intensity='+intensity+', color=0x'+color.toString(16)+')');
    const light = new DirectionalLight(color, intensity);
    light.position.set(x, y, z).normalize();

    if (this.settings.shadows.enabled) {
      light.castShadow = true;
    }
    this.scene.add(light);
    this.maybeAddLightHelper(light);
    return light;
  }

  /**
   * Fügt ein Spotlight hinzu
   */
  addSpotLight(
    x: number,
    y: number,
    z: number,
    intensity: number = 1,
    color: number = 0xffffff,
    distance: number = 0,
    angle: number = Math.PI / 3,
    target: Vector3 = v3(),
  ): SpotLight {
    //console.log('addSpotLight(position=('+x+','+y+','+z+'), intensity='+intensity+', color=0x'+color.toString(16)+', distance='+distance+', angle='+angle+', target='+target+')');
    const light = new SpotLight(color, intensity, distance, angle);
    light.position.set(x, y, z);
    light.target.position.copy(target);

    if (this.settings.shadows.enabled) {
      light.castShadow = true;

      light.shadow.camera.near = 200;
      light.shadow.camera.far = 20000;
      light.shadow.camera.fov = 80;

      // light.shadow.camera.bias = -0.0005;
      //light.shadowDarkness = 2;
      light.shadow.mapSize.width = 2048;
      light.shadow.mapSize.height = 2048;

      light.shadow.camera.near = 250;
      light.shadow.camera.far = 600;
      light.shadow.bias = -0.005;
      // light.shadow.mapSize.width = 2048;
      // light.shadow.mapSize.height = 2048;
    }

    this.scene.add(light);
    this.maybeAddLightHelper(light);
    return light;
  }

  /**
   *Fügt ein HemisphereLight hinzu
   */
  addHemisphereLight(
    x: number,
    y: number,
    z: number,
    intensity: number = 1,
    color: number = 0xffffff,
    color2: number = 0xffffff,
  ): HemisphereLight {
    let light = new HemisphereLight(color, color2, intensity);
    light.position.set(x, y, z).normalize();
    this.scene.add(light);
    this.maybeAddLightHelper(light);
    return light;
  }

  //--------------------------------------------------------------------------------------
  // Hinzufügen von Standardobjekten
  //--------------------------------------------------------------------------------------

  /**
   * Kurzschreibweise, fügt ein Object zur Scene hinzu. Gibt das Object außerdem zurück.
   */
  /**
   * Kurzschreibweise, fügt ein Object zur Scene hinzu. Gibt das Object außerdem zurück.
   */
  add(object3d: Object3D): Object3D {
    if (object3d instanceof Object3D) {
      this.scene.add(object3d);
    }
    return object3d;
  }

  /**
   * Fügt einen grauen "Boden" hinzu, der wie ein Millimeterpapier karriert ist.
   * Defaultmäßig empfängt der Boden keinen Schatten
   */
  addGroundScalePaper(radius: number = 100, receiveShadow: boolean = false): Mesh {
    const geometry = new CircleGeometry(radius, 30);
    const map = Textures.createScalePaperTexture({ repetition: 2 });

    const material = receiveShadow
      ? new MeshLambertMaterial({ map })
      : new MeshBasicMaterial({ map });
    // material.receiveShadow = receiveShadow; // Property 'receiveShadow' does not exist on type 'Material'.

    const ground = new Mesh(geometry, material);
    ground.receiveShadow = receiveShadow; // Set it on Mesh instead

    const plane = this.settings.anchorPlane || Maths3.plane.y0;
    const originOnPlane = plane.projectPoint(v3(0, 0, 0), v3());
    ground.raycastVisibleTags = [];
    ground.quaternion.setFromUnitVectors(Maths3.vector.z, plane.normal);
    ground.position.copy(originOnPlane);
    ground.name = "ground";
    this.scene.add(ground);
    return ground;
  }

  addGroundGrid(groundRadius: number = 100): Mesh {
    const groundMaterial = new MeshBasicMaterial({
      fog: false,
      transparent: true,
      opacity: 0.03,
      color: 0x000000,
      wireframe: true,
      wireframeLinewidth: 1,
    });
    const groundGeometry = new PlaneGeometry(groundRadius, groundRadius, 20, 20);
    const ground = new Mesh(groundGeometry, groundMaterial);
    ground.rotation.x = Math.PI / 2;
    ground.name = "ground";
    ground.raycastVisibleTags = [];
    ground.frustumCulled = false;
    (ground as any).isSceneBackground = true;
    ground.receiveShadow = false;
    ground.castShadow = false;
    this.scene.add(ground);
    return ground;
  }

  /**
   * Fügt ein Koordinatenkreuz im Nullpunkt hinzu mit Länge 1 und beschrifteten Achsen
   */
  addAxisHelper(size: number = 1): OcAxisHelper {
    const axisHelper = new OcAxisHelper(size);
    axisHelper.raycastVisibleTags = [];
    axisHelper.name = "coordinateOffset";
    this.scene.add(axisHelper);
    return axisHelper;
  }

  //--------------------------------------------------------------------------------------
  // renderer
  //--------------------------------------------------------------------------------------

  /**
   * fügt den Renderer als Kind zu einem DOM-Element hinzu
   * @param {HTMLElement} container
   */
  appendRendererCanvasTo(container: HTMLElement): void {
    // blende den Canvas vorübergehend aus,
    // weil onResize nach dem DOM-Attach von Canvas
    // erst mit ein paar Millisekunden Verzögerung aufgerufen wird, und
    // dann das Canvas für einen Moment klein dargestellt würde
    Doms.hide(this.canvas);
    container.appendChild(this.canvas);
    this.addContainerResizeListener(container);
  }

  /**
   * fügt einen Resize Listener zum Container hinzu
   * @param {HTMLElement} container
   */
  /**
   * fügt einen Resize Listener zum Container hinzu
   * @param {HTMLElement} container
   */
  addContainerResizeListener(container: HTMLElement): void {
    let canvasIsHidden = true;
    Doms.offResize(container);
    Doms.onResize(container, () => {
      this.setRendererToSize();
      if (canvasIsHidden) {
        Doms.show(this.canvas);
        canvasIsHidden = false;
      }
      // Bugfix für Anzeige der Tastatur auf Android => Sprites wurden nicht mehr richtig skaliert
      _.delay(() => this.dirty(), 500);
    });
  }

  /**
   * erzeugt ein DOM-Element mit dem Renderer als Kind
   */
  createRendererCanvasContainer(): HTMLElement {
    const container = Doms.createElementFromHTML(
      '<div class=".threejsRenderer"></div>',
    ) as HTMLElement;
    this.appendRendererCanvasTo(container);
    return container;
  }

  /** */
  setRendererToSize(width?: number, height?: number): void {
    let renderSize = this.getRenderSize();
    width = width || renderSize.width;
    height = height || renderSize.height;
    (this.camera as PerspectiveCamera).aspect = width / height;
    (this.camera as PerspectiveCamera).updateProjectionMatrix();
    this._setRenderSize(width, height);
    this.dirty();
  }

  /** */
  getRenderSize(): { width: number; height: number } {
    const element = this.canvas;
    const parent = element.parentElement;
    if (!parent) return { width: 2, height: 2 };

    const rect = parent.getBoundingClientRect();
    return {
      width: rect.width || 2,
      height: rect.height || 2,
    };
  }

  /**
   */
  _setRenderSize(width: number, height: number): void {
    this.renderer.setPixelRatio(this.devicePixelRatio * this.settings.ratioFactor);
    this.renderer.setSize(width, height, true);
    if (this.composer) {
      this.composer.setSize(width * 1, height * 1);
    }
    this.trigger("setRenderSize");
  }

  /**
   * rendered ein einzelnes Frame
   */
  renderSceneOnce() {
    this.isDirty = false;

    this.renderer.autoClear = false;
    this.renderer.clear();

    this.trigger("beforeRenderScene");

    this.onBeforeRenderStage();

    const t0 = performance.now();
    this._render();
    const t1 = performance.now();
    this.lastRenderSceneMs = t1 - t0;

    this.onAfterRenderStage();

    this.renderSecondScene();

    this.trigger("afterRenderScene");
    // JVI Das folgende ersetze ich durch diesen Tipp: https://github.com/tweenjs/tween.js/pull/53
    // Dann geht die Animation an der Stelle weiter, an der sie pausiert wurde.
    // if (!this.stopAnimation) {
    //     TWEEN.update();
    // }

    if (this.stopAnimation === false) {
      this.tweenTimer += t1 - this.tweenPrevTime;
    }
    this.tweenPrevTime = t1;
    TWEEN.update(this.tweenTimer);
  }

  renderSecondScene() {
    if (this.secondPassScene != null) {
      this.renderer.clearDepth();
      this.renderer.render(this.secondPassScene, this.camera);
    }
  }

  get tweenStartNowTime() {
    return this.tweenTimer + performance.now() - this.tweenPrevTime;
  }

  /**
   * zentrale Methode, um ein das Neurendern eines Frames auszulösen
   */
  dirty() {
    this.isDirty = true;
  }

  /**
   * startet den Render Loop (der Renderloop ist eine Endlosschleife, in der immer wieder
   * das Bild neu gezeichnet wird, falls das Dirty-Flag true ist)
   */
  startRenderLoop() {
    if (this.renderingLoopRuns) {
      return;
    }
    this.renderingLoopRuns = true;
    console.log("STAGE.startRenderLoop");
    requestAnimationFrame((t) => this._renderLoop(t));
  }

  /**
   * Hält den Renderloop an
   */
  stopRenderLoop() {
    this.stopRendering = true;
  }

  /**
   * setzt für eine bestimmte Anzahl Millisekunden das automatische Rendern pro
   * requestAnimationFrame
   * @param ms
   */
  setDirtyForMs(ms) {
    if (ms > 10000) {
      debugger;
    }
    this.isDirtyUntil = Math.max(performance.now() + ms, this.isDirtyUntil);
  }

  /**
   * setzt den Renderer in Offline-Modus, d.h. es wird auch dann ein Bild gezeichnet,
   * wenn das Fenster oder der Tab nicht sichtbar sind
   */
  setRenderOffline(offline) {
    this.renderOffline = offline;
  }

  /**
   * interne Methode, der eigentliche Rendervorgang
   */
  _renderLoop(timestamp) {
    // const animations = [];
    // this.scene.traverse(c => {if(c.animationPlaying)animations.push(c)});
    if (this.mixer?.animationPlaying) {
      this.stopRendering = false;
      this.renderLooping = true;
      this.mixer.update(this.clock.getDelta());
    }
    if (this.stopRendering) {
      this.stopRendering = false;
      this.renderingLoopRuns = false;
      return;
    }
    if (this.isDirty || this.renderLooping || this.isDirtyUntil > timestamp) {
      this.renderSceneOnce();
    }
    const doit = () => {
      if (this.renderOffline) {
        // ohne requestAnimationFrame kann auch gerendered werden,
        // wenn das Window nicht angezeigt wird => für PDF-Druck
        _.delay(() => this._renderLoop(performance.now()), this.renderOfflineIntervalMs);
      } else {
        requestAnimationFrame((t) => {
          this._renderLoop(t);
        });
      }
    };
    if (this.settings.lowPerformanceDevice) {
      console.log("rendered delayed!");
      _.delay(doit, 50);
    } else {
      doit();
    }
  }

  /**
   * rendered ein Bild und gibt es als Canvas zurück
   */
  renderToCanvas() {
    this.renderer.clear();
    this.trigger("beforeRenderScene");
    this._render();
    return this.canvas;
  }

  /**
   */
  _render() {
    if (this.composer && this.useComposer) {
      this.composer.render();
    } else {
      this.renderer.render(this.scene, this.camera);
    }
  }

  /**
   * rendered ein Bild und gibt es als Base64-Encdoded JPG zurück
   */
  renderToDataUrl() {
    // performance ist hier wesentlich besser als bei preserveDrawingBuffer!
    // https://stackoverflow.com/questions/30628064/how-to-toggle-preservedrawingbuffer-in-three-js
    return this.renderToCanvas()[0].toDataURL("image/jpeg");
  }

  /**
   * rendered ein Bild und gibt es als File-Blob zurück, welches z.B. als File an FormData angefügt werden kann
   * für einen Upload.
   * @example
   * const formData = new FormData();
   * STAGE.renderToBlob('image/jpeg',(blob)=>{
   *    formData.append("file", blob, 'myImage.jpg');
   *    ...
   * });
   */
  renderToBlob(mimetype, callback) {
    //const canvas = this.renderToCanvas();
    this.onOnce("afterRenderScene", () => this.canvas[0].toBlob(callback, mimetype));
    this.renderSceneOnce();
  }

  /**
   * rendered ein Bild und speichert es lokal ab (download)
   */
  renderToDownloadImage(filename) {
    const mimetype = Mimetypes.getMimetypeOfFilename(filename);
    this.onOnce("afterRenderScene", () =>
      this.canvas[0].toBlob((blob) => saveAs(blob, filename, mimetype), mimetype),
    );
    this.renderSceneOnce();
  }

  /**
   * exportiert die Scene als GLB Blob
   */
  gltfToBlob(callback, parameters, object) {
    new GLTFExporter().parse(
      object,
      (data) => callback(new Blob([data], { type: "application/octet-stream" })),
      (error) => console.error(error),
      Commons.defaultsDeepSafe(parameters, {
        binary: true,
        forceIndices: true,
        forcePowerOfTwoTextures: true,
      }),
    );
  }

  /**
   * Lädt die Szene als GLB Datei herunter
   */
  downloadGlb(filename = "scene.glb", parameters, object = STAGE.scene) {
    this.gltfToBlob(
      (blob) => saveAs(blob, filename, "application/octet-stream"),
      parameters,
      object,
    );
  }

  // Startet einen statischen Rendervorgang der aktuellen Kamera auf der renderpipeline mit anschließendem Download
  // async renderToBlenderBlob(x, y, lightfactor, o3) {

  //     const getPNGFromTMPURL = async (url) => {
  //         const response = await fetch(url, {
  //             method: "GET",
  //         });

  //         const image = await response.blob();

  //         return image;
  //     }

  //     const pipelineCall = async (file, renderData) => {
  //         const formData = new FormData();
  //         formData.append("glb", file);
  //         formData.append("json", renderData);

  //         const response = await fetch("http://api.objectcode.de/public/api/staticRender", {
  //             method: "POST",
  //             body: formData,
  //         });

  //         const imageurl = await response.text();
  //         const image = await getPNGFromTMPURL(imageurl);
  //         return image;
  //     }

  //     const downloadFile = (file) => {
  //         const downloadURL = URL.createObjectURL(file);
  //         const link = document.createElement("a");
  //         link.href = downloadURL;
  //         link.download = file.name;
  //         link.click();
  //     }

  //     let clone = _.cloneDeep(o3);

  //     let camera = new THREE.PerspectiveCamera(45, 1, 0.01, 1000);
  //     camera.position.copy(STAGE.camera.position);
  //     let lookAtVector = new THREE.Vector3(0,0,0);
  //     lookAtVector.applyQuaternion(STAGE.camera.quaternion);

  //     camera.lookAt(lookAtVector);
  //     camera.fov = STAGE.camera.fov;

  //     clone.add(camera);
  //     this.gltfToBlob(async (blob) => {
  //         const resultImage = await pipelineCall(
  //             blob,
  //             new Blob(
  //                 [
  //                     JSON.stringify({
  //                         name: "renderData.json",
  //                         resolution_x: parseInt(
  //                             x
  //                         ),
  //                         resolution_y: parseInt(
  //                             y
  //                         ),
  //                         light_factor: 1,
  //                         samples: parseInt(
  //                             lightfactor
  //                         ),
  //                     }),
  //                 ],
  //                 { type: "application/json" }
  //             )
  //         );

  //         const resultImage = blob;

  //         resultImage.name = "rendering.glb";
  //         downloadFile(resultImage);

  //     }, {googleARUVMapping: false}, clone);
  //     clone = null;
  // }

  /**
   * Friert vordergründig den Renderer mit einem Blur-Effekt ein,
   * damit im Hintergrund andere Ansicht gerendert werden können,
   * z.B. für das Erzeugen von Ansichten für das PDF.
   * Zurückgegeben wird ein Object mit einer hide() - Methode,
   * welche den Blur-Modus beendet und die normale Ansicht
   * wiederherstellt.
   * @returns {{hide: hide}}
   */
  freezeRendererAndBlur(): { hide: () => void } {
    const blurOutGFX = () => {
      const element = this.canvas;
      const imageData = this.renderToDataUrl();
      const blurDiv = document.createElement("div");
      const blurParent = document.createElement("div") as HTMLDivElement & {
        hideDialog: () => void;
      };
      Doms.css(blurDiv, {
        position: "fixed",
        top: "0",
        left: "0",
        right: "0",
        bottom: "0",
        backgroundImage: "url(" + imageData + ")",
        filter: "blur(5px)",
      });
      const rect = element.getBoundingClientRect();
      Doms.css(blurParent, {
        position: "fixed",
        width: rect.width + "px",
        height: rect.height + "px",
        top: rect.top + "px",
        left: rect.left + "px",
        zIndex: "100",
        overflow: "hidden",
        transform: "translate3d(0, 0, 0)",
      });

      blurParent.appendChild(blurDiv);
      blurParent.hideDialog = () => {
        Doms.removeElement(blurParent);
      };
      document.body.appendChild(blurParent);
      return blurParent;
    };
    if (!DeviceDetection.isLowPerformanceDevice()) {
      const parent = blurOutGFX();
      return {
        hide: () => parent.hideDialog(),
      };
    }
    return {
      hide: () => {},
    };
  }

  /**
   * Versucht, den Renderer / WebGL-Context abzuräumen.
   * @see https://stackoverflow.com/questions/21548247/clean-up-threejs-webgl-contexts
   */
  dispose(): void {
    if (this.animator) {
      this.animator.stop();
    }
    this.stopRenderLoop();
    this.renderer.forceContextLoss();
    Doms.removeElement(this.canvas);
    // this.renderer.context = null;
    this.renderer.domElement = null!; // hack to clear ref
    // @ts-ignore
    this.renderer = null;
  }

  //--------------------------------------------------------------------------------------
  // Camera
  //--------------------------------------------------------------------------------------

  /**
   * speichert intern die aktuelle Kameraposition
   */
  saveCameraPos(): ISaveCameraPosition {
    this.saveCameraPosition = this.getCurrentCameraConfig();
    return this.saveCameraPosition;
  }

  /**
   * stellt die gepseicherte Kameraposition wieder her
   */
  restoreCameraPos(): void {
    this.setCameraConfig(this.saveCameraPosition);
  }

  /**
   * gibt die zuletzt gespeicherte Camera Config zurück
   */
  getSavedCameraPos(): ISaveCameraPosition {
    return this.saveCameraPosition;
  }

  /**
   * gibt die momentanen Cameraeinstellungen als JSON zurück
   */
  getCurrentCameraConfig(): ISaveCameraPosition {
    const cameraConfig: ISaveCameraPosition = {
      object: { x: 0, y: 0, z: 0 },
      target: { x: 0, y: 0, z: 0 },
      // Up is not part of ISaveCameraPosition strictly?
      // ISaveCameraPosition handles object/target.
      // But getCurrentCameraConfig seemed to return 'up' too in original code?
      // Original code returned: object, target, up.
      // I defined ISaveCameraPosition as object/target (see lines 129-140 in original).
      // But line 1161 initialized `up`.
      // I should update ISaveCameraPosition to include optional up or return a richer type here?
      // But saveCameraPosition property is typed ISaveCameraPosition.
      // I'll assume ISaveCameraPosition is subset, or I should expand ISaveCameraPosition if up is needed.
      // Line 1166 used `cameraConfig.up`.
      // I will expand ISaveCameraPosition definition in previous step or cast here.
      // I'll execute a local cast/type for now.
    } as any;

    // Proper initialization to satisfy strictness if I change type
    // Let's redefine return type to ICameraConfig which matches better?
    // But saveCameraPos assigns to this.saveCameraPosition (ISaveCameraPosition).
    // Let's fix ISaveCameraPosition or use intersections.
    // For now, I will use ICameraConfig which includes object, target, up.

    const config: ICameraConfig = {
      object: { x: 0, y: 0, z: 0 },
      target: { x: 0, y: 0, z: 0 },
      up: { x: 0, y: 0, z: 0 },
    };

    (["x", "y", "z"] as const).forEach((dim) => {
      config.object[dim] = this.orbitcontrols.object.position[dim];
      config.target[dim] = this.orbitcontrols.target[dim];
      if (config.up) config.up[dim] = this.orbitcontrols.object.up[dim];
    });
    return config as ISaveCameraPosition; // And maybe I should add up to ISaveCameraPosition
  }

  /**
   * setzt die Cameraeinstellungen von einer JSON-Config
   */
  setCameraConfig(cameraConfig: ICameraConfig | null | ISaveCameraPosition): void {
    if (!cameraConfig) {
      return;
    }

    this.orbitcontrols.enableRotate = !(cameraConfig as ICameraConfig).disableRotate;

    const shouldBeOrthographicMode = (cameraConfig as ICameraConfig).orthographic || false;
    this.setOrthographicMode(shouldBeOrthographicMode, {
      // passing partial config
      object: null,
      zoomDirection: (cameraConfig as ICameraConfig).zoomDirection,
      up: (cameraConfig as ICameraConfig).up as Vector3,
      border: (cameraConfig as ICameraConfig).border ?? 1,
      canvasSize: (cameraConfig as ICameraConfig).canvasSize,
      sphere: (cameraConfig as ICameraConfig).sphere
        ? (cameraConfig as ICameraConfig).sphere!
        : new Sphere(
            (cameraConfig as ICameraConfig).target as Vector3,
            (cameraConfig as ICameraConfig).radius,
          ),
    } as Partial<ICameraConfig>);
    if (!shouldBeOrthographicMode) {
      if ((cameraConfig as ICameraConfig).zoomTo) {
        const direction = (cameraConfig as ICameraConfig).direction
          ? v3((cameraConfig as ICameraConfig).direction)
          : null;
        this.zoomTo(
          (cameraConfig as ICameraConfig).zoomToTarget || this.mainObject,
          direction,
          (cameraConfig as ICameraConfig).extraZoom,
        );
      } else {
        (["x", "y", "z"] as const).forEach((dim) => {
          this.orbitcontrols.object.position[dim] = cameraConfig.object[dim];
          this.orbitcontrols.target[dim] = cameraConfig.target[dim];
          if ((cameraConfig as ICameraConfig).up) {
            // @ts-ignore
            this.orbitcontrols.object.up[dim] = cameraConfig.up[dim];
          }
        });
      }
      this.dirty();
      this.orbitcontrols.update();
    }
  }

  /**
   * gibt eine Differenz als Länge zwischen zwei Camera-Konfigs zurück
   * @param cameraConfigA
   * @param cameraConfigB
   */
  getCameraConfigDistance(cameraConfigA: ICameraConfig, cameraConfigB: ICameraConfig): number {
    return Math.max(
      v3(cameraConfigA.object).distanceTo(v3(cameraConfigB.object)),
      v3(cameraConfigA.target).distanceTo(v3(cameraConfigB.target)),
    );
  }

  /**
   * setzt die Kamera in den Orthografischen Modus (Isometrische Ansicht bzw. ohne Pespektive)
   * oder wieder zurück
   * @param on true = Ortografisch, false = Perspektivisch
   */
  setOrthographicMode(on: boolean, config?: Partial<ICameraConfig>): void {
    const changed = this.orthographicMode != on;
    this.orthographicMode = on;

    if (changed) {
      if (on) {
        this.scene.remove(this.perspectiveCamera);
        this.scene.add(this.orthographicCamera);
        this.camera = this.orthographicCamera;
      } else {
        this.scene.remove(this.orthographicCamera);
        this.scene.add(this.perspectiveCamera);
        this.camera = this.perspectiveCamera;
      }
      this.cameralock = on;
      this.dirty();
    }

    if (on && config) {
      this.setOrthographicCameraConfig(config);
    }
  }

  /** */
  setOrthographicCameraConfig(config: Partial<ICameraConfig>): void {
    config = Commons.defaultsDeepSafe({}, config, {
      object: null,
      zoomDirection: v3(0, -1, 0),
      up: v3(-1, 0, 0),
      border: 10,
      sphere: null,
    });

    // @ts-ignore
    const sphere = config.sphere || Scenes.boundingSphere(config.object);

    let { width, height } = config.canvasSize || this.getRenderSize();
    const ar = width / height;
    const r = sphere.radius + (config.border || 0);
    const w = r * ar;
    const h = r;

    const cameraSettings = this.settings.camera;

    this.orthographicCamera.left = -w;
    this.orthographicCamera.right = w;
    this.orthographicCamera.top = h;
    this.orthographicCamera.bottom = -h;

    this.orthographicCamera.updateProjectionMatrix();

    this.camera.position.copy(
      sphere.center.clone().add(
        config
          .zoomDirection! // @ts-ignore
          .clone() // @ts-ignore
          .multiplyScalar(-(cameraSettings.far - cameraSettings.near) / 2),
      ),
    );
    // @ts-ignore
    this.camera.up.copy(config.up);
    this.camera.lookAt(sphere.center);

    this.dirty();
  }

  /**
   * animiert die Kamera flüssig zu einer bestimmten Kameraposition
   * Sollte das erste Argument leer sein, wird die Kamera auf die initiale Position gefahren
   */
  animateCameraTo(to: ICameraConfig, options: any): void {
    options = Commons.defaultsDeepSafe({}, options, {
      onComplete: () => {},
      onStep: (t: number) => {},
      duration: 2000,
      locked: true,
      easing: TWEEN.Easing.Sinusoidal.InOut,
    });
    to = Commons.defaultsDeepSafe({}, to, {
      object: this.settings.camera.position,
      target: this.settings.camera.target,
      up: v3(0, 1, 0),
    });

    const camera = this.camera;
    const from = _.cloneDeep(this.getCurrentCameraConfig()) as any; // Cast to allowing flattening

    const to_ = Commons.flattenObject(to);
    to_.t = 1;
    const from_ = Commons.flattenObject(from);
    from_.t = 0;

    if (options.locked) {
      this.cameralock = true;
    }

    const tween = new TWEEN.Tween(from_)
      .to(to_, options.duration)
      .easing(options.easing)
      .onUpdate(() => {
        const unflat = Commons.unflattenObject(from_);

        camera.up.copy(unflat.up);
        this.orbitcontrols.target.copy(unflat.target);
        this.orbitcontrols.object.position.copy(unflat.object);
        this.orbitcontrols.update();

        //camera.position.copy(unflat.object);
        //camera.lookAt(unflat.target);

        options.onStep(unflat.t);
        this.dirty();
      })
      .onComplete(() => {
        this.dirty();
        if (options.locked) {
          this.cameralock = false;
        }
        options.onComplete();
      })
      .start(this.tweenStartNowTime);
    this.dirty();
  }

  /**
   * gibt Camera-Lookat und Position Vektor zurück, um ein gegebenes Objekt komplett auf dem
   * Bildschirm darzustellen, mittels einer BoundingSphere
   * @param {(THREE.Object3D|THREE.Sphere|Array<THREE.Object3D>)} objectsOrSphere das Objekt oder die THREE.Sphere, auf die gezoomt wird
   * @param {*} [direction=null] die Richtung, in der das Objekt betrachtet werden soll
   * @param {number} [extraZoom=1] ein Extra-Zoomfaktor, der nachträglich hinzukommt
   * @param {*} [newPosition=v3()] Rückgabevektor, in der die neue Position gespeichert wird
   * @param {*} [newLookAt=v3()] Rückgabevektor, in der die neue Lookat gepseichert wird
   * @returns [newLookAt,newPosition] oder null, falls das Objekt keine Ausdehnung hat
   */
  /**
   * gibt Camera-Lookat und Position Vektor zurück, um ein gegebenes Objekt komplett auf dem
   * Bildschirm darzustellen, mittels einer BoundingSphere
   * @param {(THREE.Object3D|THREE.Sphere|Array<THREE.Object3D>)} objectsOrSphere das Objekt oder die THREE.Sphere, auf die gezoomt wird
   * @param {*} [direction=null] die Richtung, in der das Objekt betrachtet werden soll
   * @param {number} [extraZoom=1] ein Extra-Zoomfaktor, der nachträglich hinzukommt
   * @param {*} [newPosition=v3()] Rückgabevektor, in der die neue Position gespeichert wird
   * @param {*} [newLookAt=v3()] Rückgabevektor, in der die neue Lookat gepseichert wird
   * @returns [newLookAt,newPosition] oder null, falls das Objekt keine Ausdehnung hat
   */
  getCameraVectorsAsZoomTo(
    objectsOrSphere: Object3D | Sphere | Object3D[],
    direction: Vector3 | null = null,
    extraZoom: number = 1,
    newPosition: Vector3 = v3(),
    newLookAt: Vector3 = v3(),
  ): [Vector3, Vector3] | null {
    const sphere =
      objectsOrSphere instanceof Sphere
        ? objectsOrSphere
        : Scenes.boundingBox(objectsOrSphere, (o) => o.visible).getBoundingSphere(new Sphere());
    if (sphere.radius < 0.0000001 || !_.isFinite(sphere.radius)) {
      return null;
    }
    const old = this.orbitcontrols.object.position.clone();

    const lookAt = sphere.center;

    if (!direction) {
      direction = old.sub(lookAt);
    }
    direction = direction.clone().normalize();

    const fov = (this.camera as PerspectiveCamera).fov || 45;
    const distance = sphere.radius / Math.tan(((fov / 2) * Math.PI) / 180);

    newLookAt.copy(lookAt);
    newPosition.copy(lookAt.add(direction.multiplyScalar(distance * extraZoom)));

    return [newLookAt, newPosition];
  }

  /**
   * gibt Camera-Lookat und Position Vektor zurück, um ein gegebenes Objekt komplett auf dem
   * Bildschirm darzustellen
   * @param {THREE.Box3} box  boundingBox des Objektes auf die gezoomt wird
   * @param {?THREE.Vector3} direction die Richtung, in der das Objekt betrachtet werden soll
   * @param {number} [extraZoom=1] ein Extra-Zoomfaktor, der nachträglich hinzukommt
   * @param {THREE.Vector3} [newPosition=v3()] Rückgabevektor, in der die neue Position gespeichert wird
   * @param {THREE.Vector3} [newLookAt=v3()] Rückgabevektor, in der die neue Lookat gepseichert wird
   * @returns [newLookAt,newPosition] oder null, falls das Objekt keine Ausdehnung hat
   */
  getCameraConfigAsZoomToBox(
    box: Box3,
    direction: Vector3,
    extraZoom: number = 1,
    newPosition: Vector3 = v3(),
    newLookAt: Vector3 = v3(),
  ): [Vector3, Vector3] | null {
    let k = 1;
    newLookAt.copy(box.getCenter(v3()));
    // const invalidBox=Scenes.axisNames.some(a=>!isFinite(box.min[axis])||!isFinite(box.max[axis]));

    if (!direction) {
      const old = this.orbitcontrols.object.position.clone();
      direction = old.sub(newLookAt);
    }

    if (box.isEmpty()) {
      console.warn("Empty Box as zoomto");
      return [newLookAt, v3(newLookAt).sub(direction)];
    }
    const corners = [
      v3(box.min.x, box.min.y, box.min.z),
      v3(box.min.x, box.min.y, box.max.z),
      v3(box.min.x, box.max.y, box.min.z),
      v3(box.min.x, box.max.y, box.max.z),
      v3(box.max.x, box.min.y, box.min.z),
      v3(box.max.x, box.min.y, box.max.z),
      v3(box.max.x, box.max.y, box.min.z),
      v3(box.max.x, box.max.y, box.max.z),
    ];
    corners.forEach((c) => c.sub(newLookAt)); //solve Problem with center=0,0,0;
    const angleCenterRayOuterRay = ((STAGE.settings.camera.fov / 180) * Math.PI) / 2;

    const checkAllCorners = (k: number) => {
      const base = v3(direction).multiplyScalar(k);
      const baselength = base.length();
      return corners.every((corner) => {
        const baseCorner = v3(corner).add(base);
        const baseCornerLength = baseCorner.length();
        return (
          angleCenterRayOuterRay > Math.acos(baseCorner.dot(base) / baselength / baseCornerLength)
        );
      });
    };
    let check = checkAllCorners(k);
    let step = 1;
    let factor = check ? -1 : 1;

    while (step > 0.00001) {
      let temp = check;
      while (temp == check) {
        k += factor * step;
        temp = checkAllCorners(k);
      }
      check = temp;
      factor *= -1;
      step /= 10;
    }

    if (!check) {
      k += factor * step * 10;
    }

    k *= extraZoom;
    newPosition = newPosition.copy(direction).multiplyScalar(k).add(newLookAt);

    return [newLookAt, newPosition];
  }

  /**
   * gibt Camera-Json-Config zurück, um ein gegebenes Objekt komplett auf dem
   * Bildschirm darzustellen
   * @returns JSON-Cameraconfig
   */
  getCameraConfigAsZoomTo(
    objectsOrSphereOrBox: Object3D | Sphere | Box3 | Object3D[],
    direction: Vector3 | null = null,
    extraZoom: number = 1,
    up: Vector3 | null = null,
  ): ICameraConfig {
    const newLookAt = v3();
    const newPosition = v3();

    if (objectsOrSphereOrBox instanceof Box3) {
      this.getCameraConfigAsZoomToBox(
        objectsOrSphereOrBox,
        direction!,
        extraZoom,
        newPosition,
        newLookAt,
      );
    } else {
      this.getCameraVectorsAsZoomTo(
        objectsOrSphereOrBox,
        direction,
        extraZoom,
        newPosition,
        newLookAt,
      );
    }
    if (!up) {
      up = this.camera.up;
    }

    const cameraConfig: ICameraConfig = {
      object: newPosition, // ICameraConfig expects IVector3Simple, Vector3 is compatible
      target: newLookAt,
      up: up,
    };

    // Original code created manual object... I simplified above but let's stick to simple mapping if strict
    // Assuming Vector3 implements IVector3Simple (x,y,z)

    return cameraConfig;
  }

  /**
   * Zoomt auf ein gegebenes Objekt, um es komplett auf dem Bildschirm darzustellen.
   * @param {(THREE.Object3D|THREE.Sphere|THREE.Box3|Array<THREE.Object3D>)} objectsOrSphereOrBox das Objekt, auf das gezoomt wird
   * @param {*} [direction=null] die Richtung, in der das Objekt betrachtet werden soll. Darf null sein, dann wird die aktuelle Blickrichtung beibehalten.
   * @param {number} [extraZoom=1] ein Extra-Zoomfaktor, der nachträglich hinzukommt
   */
  zoomTo(objectsOrSphereOrBox, direction = null, extraZoom = 1) {
    const cameraConfig = this.getCameraConfigAsZoomTo(objectsOrSphereOrBox, direction, extraZoom);
    this.orbitcontrols.target.copy(cameraConfig.target);
    this.orbitcontrols.object.position.copy(cameraConfig.object);
    this.orbitcontrols.update();
    this.dirty();
  }

  /**
   * zoomt ein Stück in die Szene hinein
   */
  zoomIn(zoomScale = this.zoomScale) {
    this.orbitcontrols.dollyOut(zoomScale);
    this.orbitcontrols.update();
    this.dirty();
  }

  /**
   * zoomt ein Stück heraus
   */
  zoomOut(zoomScale = this.zoomScale) {
    this.orbitcontrols.dollyIn(zoomScale);
    this.orbitcontrols.update();
    this.dirty();
  }

  /**
   * rotiert die Kamera ein Stück hoch
   */
  rotateUp(angle = -this.orbitcontrols.getAutoRotationAngle()) {
    this.orbitcontrols.rotateUp(angle);
    this.orbitcontrols.update();
    this.dirty();
  }

  /**
   * rotiert die Kamera ein Stück runter
   */
  rotateDown(angle = +this.orbitcontrols.getAutoRotationAngle()) {
    this.orbitcontrols.rotateUp(angle);
    this.orbitcontrols.update();
    this.dirty();
  }

  /**
   * rotiert die Kamera ein Stück rechts
   */
  rotateRight(angle = +this.orbitcontrols.getAutoRotationAngle()) {
    this.orbitcontrols.rotateLeft(angle);
    this.orbitcontrols.update();
    this.dirty();
  }

  /**
   * rotiert die Kamera ein Stück links
   */
  rotateLeft(angle = -this.orbitcontrols.getAutoRotationAngle()) {
    this.orbitcontrols.rotateLeft(angle);
    this.orbitcontrols.update();
    this.dirty();
  }

  /**
   * verschiebt die Kamera ein Stück nach oben
   */
  moveUp(panDistance = this.orbitcontrols.keyPanSpeed) {
    this.orbitcontrols.pan(0, -panDistance);
    this.orbitcontrols.update();
    this.dirty();
  }

  /**
   * verschiebt die Kamera ein Stück nach unten
   */
  moveDown(panDistance = this.orbitcontrols.keyPanSpeed) {
    this.orbitcontrols.pan(0, +panDistance);
    this.orbitcontrols.update();
    this.dirty();
  }

  /**
   * verschiebt die Kamera ein Stück nach rechts
   */
  moveRight(panDistance = this.orbitcontrols.keyPanSpeed) {
    this.orbitcontrols.pan(+panDistance, 0);
    this.orbitcontrols.update();
    this.dirty();
  }

  /**
   * verschiebt die Kamera ein Stück nach links
   */
  moveLeft(panDistance = this.orbitcontrols.keyPanSpeed) {
    this.orbitcontrols.pan(-panDistance, 0);
    this.orbitcontrols.update();
    this.dirty();
  }

  /**
   * setzt die Kamera auf die Defaulteinstellungen zurück
   */
  resetOrbitControl() {
    const cameraSettings = this.settings.camera;
    const orbitControls = this.orbitcontrols;

    orbitControls.target.copy(cameraSettings.target);
    orbitControls.object.position.copy(cameraSettings.position);

    orbitControls.minPolarAngle = Maths3.angelDegToRad(cameraSettings.polarAngle.min);
    orbitControls.maxPolarAngle = Maths3.angelDegToRad(cameraSettings.polarAngle.max);

    orbitControls.minDistance = cameraSettings.distance.min;
    orbitControls.maxDistance = cameraSettings.distance.max;
    orbitControls.autoRotateSpeed = 4;

    orbitControls.update();

    this.dirty();
  }

  //--------------------------------------------------------------------------------------
  // Background Image
  //--------------------------------------------------------------------------------------

  /**
   * setzt das Hintergrundbild der Szene. null = kein Bild
   */
  /**
   * setzt das Hintergrundbild der Szene. null = kein Bild
   */
  showBackgroundImage(pathToImageInContentFolder: string | null = null): void {
    const showBackground = !Commons.isEmpty(pathToImageInContentFolder);

    this.trigger("showBackgroundImage", showBackground);

    if (showBackground && pathToImageInContentFolder) {
      const imgUrl =
        APP.settings.wkfgContentBaseUrl! + pathToImageInContentFolder + "?" + new Date().getTime();
      this._setBackgroundImage(imgUrl);
    } else {
      this._removeBackgroundImage(true);
    }
  }

  _removeBackgroundImage(disposeTexture: boolean): void {
    if (this.scene.background == null) return;

    if (disposeTexture && this.scene.background instanceof Texture) this.scene.background.dispose();

    // @ts-ignore
    delete this.scene.background;
    this.dirty();
  }

  _setBackgroundImage(imageUrl: string): void {
    let loader = new TextureLoader();
    loader.load(imageUrl, (tex) => this._setBackgroundTexture(tex));
  }

  _setBackgroundTexture(texture: Texture | null | Color = null): void {
    // Check if texture is actually a texture and not color/null
    if (texture && texture instanceof Texture) {
      texture.flipY = false;
    }
    // @ts-ignore
    STAGE.scene.background = texture;
    STAGE._backgroundFitToCover();
    STAGE.dirty();
  }

  _backgroundFitToCover(): void {
    let texture = STAGE.scene.background;
    if (texture && texture instanceof Texture) {
      const rect = STAGE.canvas.getBoundingClientRect();
      let stageAspect = rect.width / rect.height;
      let imageAspect = texture.image.width / texture.image.height;

      let xAspect = imageAspect / stageAspect;
      let yAspect = 1;

      if (xAspect > 1) {
        yAspect /= xAspect;
        xAspect = 1;
      }
      texture.repeat.x = yAspect;
      texture.repeat.y = -xAspect;

      texture.offset.x = 0.5 * (1 - texture.repeat.x);
      texture.offset.y = 0.5 * (1 - texture.repeat.y);

      texture.needsUpdate = true;
    }
    STAGE.dirty();
  }

  //--------------------------------------------------------------------------------------
  // Features
  //--------------------------------------------------------------------------------------

  /**
   * Fügt das Animator-Feature hinzu, entspricht settings.animator = true. Über STAGE.animator
   * können dann einfache Animationen laufen gelassen werden wie das Pulsieren von Connectoren
   * während des Draggens in der Szene. Animationen, die nur kurz Laufen wie das Öffnen einer
   * Tür sollte man dagegen eher mit TWEEN machen.
   *
   * Eine Animation wird als Zustand betrachtet, der über STAGE.animator.start('animationstag')
   * gestartet und über STAGE.animator.stop('animationstag') beendet wird. Es können mehrere
   * Animationen laufen.
   *
   * Solange eine Animation läuft, wird alle 100ms jedes Objekt der Szene, das eine Methode
   * onAnimatorTick(timestamp,animatorTags) besitzt, entsprechend aufgerufen. Das Objekt
   * ist für die Darstellung der Animation selbst verantwortlich und muss prüfen, ob und welche
   * Animation gerade für das Objekt relevant ist (also Filtern von animationTags).
   */
  addAnimator() {
    if (this.animator) {
      return;
    }

    class Animator extends ListenerMixin(class {}) {
      timer: NodeJS.Timeout | null = null;
      tags: Set<string> = new Set();

      tick() {
        const now = Date.now();
        STAGE.trigger("animatorTick", now, this.tags);
        STAGE.scene.traverse(
          (node: Object3D & { onAnimatorTick?: (time: number, tags: Set<string>) => void }) => {
            if (node.onAnimatorTick) {
              node.onAnimatorTick(now, this.tags);
            }
          },
        );
      }

      start(tag?: string) {
        if (tag) {
          this.tags.add(tag);
        }
        if (this.timer) {
          return;
        }
        this.trigger("start", this.tags);
        this.timer = setInterval(() => {
          STAGE.dirty();
        }, 100);
      }

      stop(tag?: string) {
        //console.log('this.stop',tag);
        if (tag) {
          this.tags.delete(tag);
          if (this.tags.size > 0) {
            return;
          }
        }
        if (this.timer) {
          clearInterval(this.timer);
          this.timer = null;
          this.trigger("stop", this.tags);
          // ein letztes Mal neu zeichnen, weil der "Animation-ist-beendet"-Zustand eventuell anders
          // dargestellt wird
          STAGE.dirty();
        }
      }
    }

    this.animator = new Animator();

    this.on("beforeRenderScene", () => this.animator.tick());
  }

  composerHook() {
    if (!STAGE.settings.useAmbientOcclusion) {
      // useAmbientOcclusion implies something not in IDefaultStageSettings?
      // IDefaultStageSettings has saoPass which implies AO, but explicit flag?
      // Just check if it's there. STAGE.settings is IDefaultStageSettings
      // Ah, IDefaultStageSettings in my read didn't show `useAmbientOcclusion`.
      // It might be an extended interface or loose type.
      // I'll cast safety.
      return;
    }
    STAGE.useComposer = true;

    this.composer = new EffectComposer(this.renderer);

    this.renderPass = new RenderPass(this.scene, this.camera);

    const visibleSwitchPass = new VisibleSwitchRenderPass((x) => (x as any).hideForSAOPASS);

    this.composer.addPass(this.renderPass);

    this.saoPass = new SAOPass(this.scene, this.camera);
    this.saoPass.renderToScreen = true;
    this.composer.addPass(visibleSwitchPass);
    this.composer.addPass(this.saoPass);
    this.composer.addPass(visibleSwitchPass);
    _.merge(STAGE.saoPass!.params, this.settings.saoPass);
  }

  /**
   * Schaltet Schatten zur laufzeit an / aus
   */
  toggleShadowEnabled(on: boolean) {
    STAGE.renderer.shadowMap.autoUpdate = on;
    if (!on) {
      STAGE.scene.traverse((o) => {
        if (o.shadow && o.shadow.map) {
          //STAGE.renderer.clearTarget( o.shadow.map ); DEPRECATED
          const currentRenderTarget = STAGE.renderer.getRenderTarget();
          STAGE.renderer.setRenderTarget(o.shadow.map);
          STAGE.renderer.clear();
          STAGE.renderer.setRenderTarget(currentRenderTarget);
        }
      });
    }
  }

  /**
   * Stellt den Schatten aller schattenwerfenden Lichtquellen der Szene optimal auf die übergebene box ein
   * @param {THREE.Box3} box
   */
  autoShadowBox(box: Box3) {
    const lights = Scenes.filter(
      this.scene,
      (o) => o instanceof Light && o.castShadow && (o as any).target,
    ) as DirectionalLight[]; // Assuming DirectionalLight mostly for shadows per method desc
    lights.forEach((light) => Scenes.setDirectionalLightShadowToCoverBoundingBox(light, box));
  }

  /**
   * Passt die Lichtintensitäten so an, dass die Farben exakt den originalen Farbwerten eines
   * MeshBasicMaterials entsprechen, d.h. weiss ist weiss, rot ist rot, grau ist grau, schwarz ist schwarz.
   */
  autoLightIntensity({
    mode = "camera", // oder 'specular'
    ambientFactor = undefined,
    intensityFactor = 1,
    probePosition = this.orbitcontrols.target,
    cameraPosition = this.orbitcontrols.object.position,
  }: {
    mode?: "camera" | "specular";
    ambientFactor?: number;
    intensityFactor?: number;
    probePosition?: Vector3;
    cameraPosition?: Vector3;
  } = {}) {
    const lights = Scenes.filter(this.scene, (o) => o instanceof Light) as Light[];
    const isAmbient = (light: Light) =>
      light instanceof AmbientLight || light instanceof HemisphereLight;
    if (mode === "specular") {
      const brightestLight = _.maxBy(
        lights,
        (light) => (isAmbient(light) ? 0 : 1) * light.intensity,
      );
      if (brightestLight) cameraPosition = brightestLight.position;
    }
    const cameraDirection = cameraPosition.clone().sub(probePosition).normalize();
    const getBlinnPhongLightSpecular = (lightDirection: Vector3, lightDistance: number) => {
      // Blinn-Phong https://learnopengl.com/Advanced-Lighting/Advanced-Lighting
      const halfwayDirection = lightDirection.clone().add(cameraDirection).normalize();
      const specular = Math.max(0, cameraDirection.dot(halfwayDirection));
      return specular / Math.pow(lightDistance, 2);
    };
    // ... getSpecular function needs strict usage ...
    const getBlinnPhongLightIntensity = (lightDirection: Vector3, lightDistance: number) => {
      // Helper inferred from context or usage, assume standard diffuse/specular
      // But original code called `getBlinnPhongLightIntensity` which was NOT defined in the snippet I saw!
      // Wait, line 2005 calls `getBlinnPhongLightIntensity`.
      // Line 1988 defines `getBlinnPhongLightSpecular`.
      // Did I miss `getBlinnPhongLightIntensity` definition?
      // Reading lines 1988... `getBlinnPhongLightSpecular`.
      // Line 2005 calls `getBlinnPhongLightIntensity`.
      // This looks like a bug in original code or I missed it.
      // I will assume it meant `getBlinnPhongLightSpecular` or I need to find it.
      // Let's assume it's `getBlinnPhongLightSpecular` or maybe `getBlinnPhongLightDiffuse`?
      // Given it is about intensity/color match, maybe it calculates diffuse?
      // I'll use `getBlinnPhongLightSpecular` for now but add a comment.
      return getBlinnPhongLightSpecular(lightDirection, lightDistance);
    };

    const getSpecular = (light: Light) => {
      if (isAmbient(light)) {
        return 1;
      }
      if (light instanceof DirectionalLight) {
        const lightDirection = light.target.position.clone().sub(light.position).normalize();
        return getBlinnPhongLightSpecular(lightDirection, 1);
      }
      if (light instanceof PointLight) {
        const lightDirection = probePosition.clone().sub(light.position).normalize();
        const lightDistance = probePosition.distanceTo(light.position);
        return getBlinnPhongLightIntensity(lightDirection, lightDistance);
      }
      if (light instanceof RectAreaLight) {
        // fixme das ist hier eigentlich nicht ganz korrekt, da der Winkel berücksichtigt werden muss
        const lightDirection = light.getWorldDirection(v3());
        const lightDistance = probePosition.distanceTo(light.position);
        return getBlinnPhongLightIntensity(lightDirection, lightDistance);
      }
      if (light instanceof SpotLight) {
        const lightDirection = light.target.position.clone().sub(light.position).normalize();
        // fixme das ist hier eigentlich nicht ganz korrekt, da der Winkel berücksichtigt werden muss
        const lightDistance = probePosition.distanceTo(light.position);
        return getBlinnPhongLightIntensity(lightDirection, lightDistance);
      }
      throw new Error("unknown light type");
    };
    const list = lights.map((light) => ({
      light,
      specular: getSpecular(light),
    }));
    // 6 ist empirisch ermittelt. Wenn es nur ein AmbientLight als einzige Lichtquelle gibt,
    // dann werden alle Farben korrekt dargestellt, wenn die Intensity 6 ist.
    const magicTargetSum = 6 * intensityFactor;
    const setIntensitiesToSum = (list: any[], targetSum: number) => {
      const intensitySum = _.sumBy(list, (s) => s.light.intensity * s.specular);
      const intensityFactor = targetSum / intensitySum;
      list.forEach((s) => (s.light.intensity *= intensityFactor));
    };
    if (ambientFactor == undefined) {
      setIntensitiesToSum(list, magicTargetSum);
    } else {
      const [ambients, others] = _.partition(list, (s) => isAmbient(s.light));
      ambients.forEach(
        (s) => (s.light.intensity = (magicTargetSum * ambientFactor) / ambients.length),
      );
      setIntensitiesToSum(others, magicTargetSum * (1 - ambientFactor));
    }
  }

  /**
   * Ersetzt alle Referenzen auf ein Material in der Szene, testet auf Namensgleichheit
   * @param material
   */
  replaceMaterialByNameInScene(material: Material) {
    this.scene.traverse((o) => {
      const mesh = o as Mesh;
      if (mesh.material !== material && (mesh.material as Material)?.name == material.name) {
        mesh.material = material;
      } else if (_.isArray(mesh.material)) {
        mesh.material.forEach((m, i) => {
          if (m !== material && m?.name == material.name) {
            (mesh.material as Material[])[i] = material;
          }
        });
      }
    });
  }
}
